import type { SyncedTable } from '@/lib/db';

/**
 * Forma de cada tabla en Postgres.
 *
 * Columnas tipadas solo para lo que el servidor necesita mirar (RLS, orden,
 * clave natural) y el resto en un `data jsonb`. El servidor de esta app no
 * calcula nada: todo el analisis vive en el motor, en el cliente. Tipar 30
 * columnas de nutrientes por tabla solo anadiria migraciones sin comprar nada.
 * Ver DECISIONS.md D-27.
 */
export interface TableSpec {
  /** Nombre en Postgres. */
  remote: string;
  /** Campo que hace de clave primaria en Dexie. */
  localKey: string;
  /** Campos que ademas van como columna propia (indexados o usados en RLS). */
  columns: readonly string[];
}

export const TABLE_SPECS: Record<SyncedTable, TableSpec> = {
  profile: { remote: 'profiles', localKey: 'id', columns: [] },
  foods: { remote: 'foods', localKey: 'id', columns: ['kind', 'barcode'] },
  meals: { remote: 'meal_entries', localKey: 'id', columns: ['date', 'slot'] },
  days: { remote: 'days', localKey: 'date', columns: ['date', 'complete'] },
  bodyWeights: { remote: 'body_weights', localKey: 'date', columns: ['date'] },
  exercises: { remote: 'exercises', localKey: 'id', columns: [] },
  workouts: { remote: 'workouts', localKey: 'id', columns: ['date'] },
  routines: { remote: 'routines', localKey: 'id', columns: [] },
  savedMeals: { remote: 'saved_meals', localKey: 'id', columns: [] },
};

const META_FIELDS = new Set(['updatedAt', 'deletedAt']);

export interface RemoteRow {
  id: string;
  user_id: string;
  updated_at: string;
  deleted_at: string | null;
  data: Record<string, unknown>;
  [column: string]: unknown;
}

/** Fila local -> fila de Postgres. */
export function toRemote(
  table: SyncedTable,
  row: Record<string, unknown>,
  userId: string,
): RemoteRow {
  const spec = TABLE_SPECS[table];
  const data: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(row)) {
    if (key === spec.localKey || META_FIELDS.has(key)) continue;
    data[key] = value;
  }

  const out: RemoteRow = {
    id: String(row[spec.localKey]),
    user_id: userId,
    updated_at: String(row.updatedAt),
    deleted_at: (row.deletedAt as string | null) ?? null,
    data,
  };

  for (const column of spec.columns) {
    out[snake(column)] = row[column] ?? null;
  }

  return out;
}

/** Fila de Postgres -> fila local. */
export function toLocal(table: SyncedTable, remote: RemoteRow): Record<string, unknown> {
  const spec = TABLE_SPECS[table];
  return {
    ...remote.data,
    [spec.localKey]: remote.id,
    updatedAt: remote.updated_at,
    deletedAt: remote.deleted_at,
  };
}

function snake(value: string): string {
  return value.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}
